// $-cost estimation, derived from the cpu_seconds + memory_gib_seconds
// counters that workers record on every Job. Rates come from the portal
// (PortalConfig) and are exposed via /api/me so the SPA can compute
// client-side without a per-page round-trip.
//
// The numbers are an *estimate* — actual EKS spend depends on instance
// type, spot vs on-demand, and add-ons (EBS, networking, control plane
// share). The rates are deliberately tunable so deployers can plug in
// their own contract pricing.

export interface CostRates {
  cpuHrUsd: number;
  memGibHrUsd: number;
}

interface JobCounters {
  cpu_seconds?: number;
  memory_gib_seconds?: number;
}

// Parse the operator's `counters_json` (a string blob attached to each
// Job row) and pull out the two cost-relevant numbers. Counters_json
// can be null on jobs that haven't reported any counters yet — we
// return zeros in that case.
function parseCounters(counters_json: string | null | undefined): JobCounters {
  if (!counters_json) return {};
  try {
    return JSON.parse(counters_json) as JobCounters;
  } catch {
    return {};
  }
}

export interface JobLike {
  counters_json: string | null | undefined;
}

export function costForJob(job: JobLike, rates: CostRates): number {
  const c = parseCounters(job.counters_json);
  const cpuHours = (c.cpu_seconds ?? 0) / 3600;
  const memGibHours = (c.memory_gib_seconds ?? 0) / 3600;
  return cpuHours * rates.cpuHrUsd + memGibHours * rates.memGibHrUsd;
}

export function costForWorkflow(jobs: JobLike[], rates: CostRates): number {
  return jobs.reduce((acc, j) => acc + costForJob(j, rates), 0);
}

// Fargate slot shape for export Jobs. Matches the operator's
// jobspec.py:_resources_for(EXPORT): 16 vCPU + 32 GiB. Used by the
// Review step to project a Fargate-time cost from the post-filter
// doc count.
const EXPORT_SLOT_VCPU = 16;
const EXPORT_SLOT_MEM_GIB = 32;
// Sustained throughput from the export-tests baseline (~70k post-
// filter docs/sec on a 16 vCPU slot). Used to translate doc count
// into expected wall-time per slot.
const EXPORT_THROUGHPUT_PPS = 70_000;
// Fargate's minimum billable duration is 1 minute. Small exports
// don't bill below this so the predicted floor matches reality.
const EXPORT_MIN_SECONDS = 60;
// Amortized startup overhead per slot: image pull (~30s) + cold
// start + dashboards seed in the indexer case. Cheap heuristic;
// keeps the prediction honest about per-slot fixed cost on
// multi-month exports.
const EXPORT_STARTUP_USD_PER_SLOT = 0.02;

// Predicted search cost for an export — Fargate-time based, given the
// post-filter doc count and how many month slots the batch_export
// will spawn. Sums the per-slot compute cost (floored at the 60s
// Fargate minimum) plus a fixed startup amortization per slot.
//
// Used by the Review step to surface an "estimated total" so the user
// sees a cost figure before clicking Submit. This is *separate* from
// post-hoc per-job cost (`costForJob`) which computes from real
// cpu_seconds + memory_gib_seconds counters once the workflow has run.
//
// Rejected design: ``numDocs × $/1000-docs`` (the pre-rc50 model).
// That model ignored the Fargate 60s minimum and the per-slot startup,
// so it overcounted small exports and undercounted long ones — and
// the deployer-tuned magic number had no semantic basis. Empirically
// it ran 20-1000× higher than real spend.
export function costForSearchEstimate(
  numDocs: number,
  numMonths: number,
  rates: CostRates,
): number {
  if (!isFinite(numDocs) || numDocs <= 0) return 0;
  const slots = Math.max(1, Math.floor(numMonths));
  const docsPerSlot = numDocs / slots;
  const secPerSlot = Math.max(
    EXPORT_MIN_SECONDS,
    docsPerSlot / EXPORT_THROUGHPUT_PPS,
  );
  const slotHourlyUsd =
    EXPORT_SLOT_VCPU * rates.cpuHrUsd + EXPORT_SLOT_MEM_GIB * rates.memGibHrUsd;
  const computePerSlot = (secPerSlot * slotHourlyUsd) / 3600;
  return slots * (computePerSlot + EXPORT_STARTUP_USD_PER_SLOT);
}

// Justification text for the pre-flight search estimate. Mirrors the
// math in ``costForSearchEstimate`` step-by-step so the Review step
// can render the same expandable code block as the other cost
// surfaces. All numbers below come from the same inputs the
// estimator uses — no second source of truth.
export function formulaLinesForSearchEstimate(
  numDocs: number,
  numMonths: number,
  rates: CostRates,
): string[] {
  const slots = Math.max(1, Math.floor(numMonths));
  const docsPerSlot = numDocs / slots;
  const rawSecPerSlot = docsPerSlot / EXPORT_THROUGHPUT_PPS;
  const secPerSlot = Math.max(EXPORT_MIN_SECONDS, rawSecPerSlot);
  const slotHourlyUsd =
    EXPORT_SLOT_VCPU * rates.cpuHrUsd + EXPORT_SLOT_MEM_GIB * rates.memGibHrUsd;
  const computePerSlot = (secPerSlot * slotHourlyUsd) / 3600;
  const total = slots * (computePerSlot + EXPORT_STARTUP_USD_PER_SLOT);
  const flooredNote =
    rawSecPerSlot < EXPORT_MIN_SECONDS
      ? ` (Fargate 60s billing minimum)`
      : "";
  return [
    "Export cost projection (Fargate compute time)",
    `  docs_to_scan         = ${Math.round(numDocs).toLocaleString()}`,
    `  months               = ${numMonths}`,
    `  slots                = max(1, floor(months)) = ${slots}` +
      `   (one Fargate slot per month)`,
    `  docs_per_slot        = docs_to_scan / slots = ${Math.round(docsPerSlot).toLocaleString()}`,
    "",
    `  throughput_pps       = ${EXPORT_THROUGHPUT_PPS.toLocaleString()} docs/sec` +
      `   (16 vCPU export-tests baseline)`,
    `  sec_per_slot         = max(${EXPORT_MIN_SECONDS}, docs_per_slot / throughput_pps)`,
    `                       = max(${EXPORT_MIN_SECONDS}, ${rawSecPerSlot.toFixed(2)})`,
    `                       = ${secPerSlot.toFixed(2)} s${flooredNote}`,
    "",
    `  slot_vcpu            = ${EXPORT_SLOT_VCPU}`,
    `  slot_mem_gib         = ${EXPORT_SLOT_MEM_GIB}`,
    `  cpu_hr_usd           = $${rates.cpuHrUsd.toFixed(4)}/vCPU·hr`,
    `  mem_gib_hr_usd       = $${rates.memGibHrUsd.toFixed(4)}/GiB·hr`,
    `  slot_hourly_usd      = slot_vcpu × cpu_hr_usd + slot_mem_gib × mem_gib_hr_usd`,
    `                       = ${EXPORT_SLOT_VCPU} × $${rates.cpuHrUsd.toFixed(4)} + ${EXPORT_SLOT_MEM_GIB} × $${rates.memGibHrUsd.toFixed(4)}`,
    `                       = $${slotHourlyUsd.toFixed(4)}/hr`,
    "",
    `  compute_per_slot     = sec_per_slot × slot_hourly_usd / 3600`,
    `                       = ${secPerSlot.toFixed(2)} × $${slotHourlyUsd.toFixed(4)} / 3600`,
    `                       = $${computePerSlot.toFixed(4)}`,
    `  startup_per_slot     = $${EXPORT_STARTUP_USD_PER_SLOT.toFixed(2)}` +
      `   (image pull + cold-start amortization)`,
    "",
    `  total                = slots × (compute_per_slot + startup_per_slot)`,
    `                       = ${slots} × ($${computePerSlot.toFixed(4)} + $${EXPORT_STARTUP_USD_PER_SLOT.toFixed(2)})`,
    `                       = $${total.toFixed(4)}`,
    "",
    "Rates come from the deployer's PortalConfig (cost_cpu_hr_usd,",
    "cost_mem_gib_hr_usd) exposed via /api/me. Throughput + slot shape",
    "are constants from src/paim/operator/jobspec.py:_resources_for(EXPORT)",
    "and the export-tests baseline. Doesn't account for an optional",
    "Lucene search reducing the kept set further at export time.",
  ];
}

// Build the formula-lines explanation for a workflow's compute cost.
// Mirrors the shape the operator returns for search-env cost panels
// (one line per arithmetic step, plus a constants/inputs header) so
// the expandable code block looks identical across surfaces.
//
// Lives client-side because workflow compute is derived from the
// pod-level cpu_seconds + memory_gib_seconds counters using the
// deployer-tuned rates exposed via /api/me — the operator never
// computes this number itself.
export function formulaLinesForWorkflow(
  cpuSeconds: number,
  memoryGibSeconds: number,
  rates: CostRates,
): string[] {
  const cpuHours = cpuSeconds / 3600;
  const memGibHours = memoryGibSeconds / 3600;
  const cpuCost = cpuHours * rates.cpuHrUsd;
  const memCost = memGibHours * rates.memGibHrUsd;
  const total = cpuCost + memCost;
  return [
    "Workflow compute (real spend, billed from pod cgroup counters)",
    `  cpu_seconds         = ${cpuSeconds.toLocaleString()} s`,
    `  memory_gib_seconds  = ${memoryGibSeconds.toLocaleString()} GiB·s`,
    `  cpu_hr_usd          = $${rates.cpuHrUsd.toFixed(4)}/vCPU·hr`,
    `  mem_gib_hr_usd      = $${rates.memGibHrUsd.toFixed(4)}/GiB·hr`,
    "",
    `  cpu_cost = (cpu_seconds / 3600) × cpu_hr_usd`,
    `         = (${cpuSeconds.toLocaleString()} / 3600) × $${rates.cpuHrUsd.toFixed(4)}`,
    `         = $${cpuCost.toFixed(4)}`,
    `  mem_cost = (memory_gib_seconds / 3600) × mem_gib_hr_usd`,
    `         = (${memoryGibSeconds.toLocaleString()} / 3600) × $${rates.memGibHrUsd.toFixed(4)}`,
    `         = $${memCost.toFixed(4)}`,
    `         total = $${total.toFixed(4)}`,
    "",
    "Rates come from the deployer's PortalConfig (cost_cpu_hr_usd,",
    "cost_mem_gib_hr_usd) exposed via /api/me. Counters come from",
    "each Job's pod-level cgroup accounting summed across the workflow.",
  ];
}

// Render a cost as a sensibly-precisioned string. Sub-cent values get
// 4 decimals; pennies get 3; dollar+ values get 2.
export function formatCost(usd: number): string {
  if (!isFinite(usd) || usd <= 0) return "$0.00";
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
}
